import { useCallback, useEffect, useRef, useState } from 'react'
import {
  api, can, canAny, rememberCardDelivery, rememberPermissions, type Principal,
} from '../lib/api'
import { Overview } from './Overview'
import { Settings, hasSettingsTabs } from './Settings'
import { ThemeToggle } from '../components/ThemeToggle'
import { Search } from './Search'
import { ReportsHistory, mayReportsHistory, type HistoryPick } from './ReportsHistory'
import { Equipment, OutNowStrip } from './Equipment'
import { Dispatch } from './Dispatch'
import { MessagingTab, mayMessagingTab } from './MessagingTab'
import { Feedback } from './Feedback'
import { ChangePassword } from './ChangePassword'
import { StaySignedIn } from './StaySignedIn'
import { PanelHost, type PanelTx, type PanelGroup } from './PanelHost'
import type { RadioSeat } from '../lib/api'

/** THE TOP BAR IS THE THINGS A SHIFT USES. Phone book, Members and
 *  Operations moved into Settings as tabs -- they are sit-down tasks, and
 *  every one of them the customer named. They are NOT gated behind Settings
 *  by that move: the tabs carry their own permissions, so a dispatcher still
 *  reaches the phone book and the roster. */
type View =
  | 'calls' | 'panel' | 'overview'
  | 'search' | 'reports' | 'equipment' | 'settings' | 'messaging' | 'feedback'

/** What each screen is called, for a report that says where it was sent
 *  from (065). Past calls and Call log live inside Reports / History since
 *  6 Oct 2026, so they are named by HISTORY_NAMES after it. */
const VIEW_NAMES: Record<View, string> = {
  calls: 'Dispatch', panel: 'Radio panel', overview: 'Overview',
  search: 'Search', reports: 'Reports / History', equipment: 'Equipment',
  settings: 'Settings', messaging: 'Messaging', feedback: 'Feedback / Support',
}
const HISTORY_NAMES: Record<HistoryPick, string> = {
  past: 'Past calls', log: 'Call log', calls: 'Calls report', messages: 'Messages report',
}

/** What the panel is actually saying. Not decoration: it settles whether a
 *  message we need already exists, without asking anybody. */
function WireLog({ wire }: { wire: { at: string; type: string; raw: string }[] }) {
  const [open, setOpen] = useState(false)
  const seen = Array.from(new Set(wire.map((w) => w.type)))

  return (
    <div className="wirelog">
      <button className="wirehead" onClick={() => setOpen(!open)}>
        <span className="lbl">Panel messages</span>
        <span className="count">{wire.length}</span>
        {seen.map((t) => <span className="pill" key={t}>{t}</span>)}
        <span className="spacer" />
        <span className="lbl">{open ? 'hide' : 'show'}</span>
      </button>
      {open && (
        <div className="wirebody">
          {wire.length === 0 ? (
            <div className="wirerow">
              Nothing yet. Talk on the panel, change a group, and watch what arrives.
            </div>
          ) : (
            wire.map((w, i) => (
              <div className="wirerow" key={i}>
                <span className="w-at">{w.at}</span>
                <span className="w-type">{w.type}</span>
                <span className="w-raw">{w.raw}</span>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  )
}

const RADIO_REASON: Record<string, string> = {
  off: 'This customer has no dispatch panel connection.',
  no_seat: 'No dispatch connection is assigned to this login.',
  pending: 'The seat has no WalkieFleet account attached yet.',
  ready: 'The full dispatch panel',
}

function RadioBanner({ state }: { state: string }) {
  if (state === 'off') {
    // Not a warning. Nothing is broken and nobody is waiting on anybody -- the
    // radio is not part of this installation today, and the rest of the CRM is
    // untouched by that.
    return (
      <div className="banner">
        <strong>No dispatch panel on this account.</strong> The radio side is
        disconnected, so there is nothing to open. Every other part of the CRM
        works normally.
      </div>
    )
  }
  if (state === 'no_seat') {
    return (
      <div className="banner wait">
        <strong>No dispatch connection assigned.</strong> This login is not
        attached to a seat — “Dispatcher 1”, “Dispatcher 2” — so there is no
        identity for the field to hear. An administrator sets that on the login.
      </div>
    )
  }
  return (
    <div className="banner wait">
      <strong>Dispatch panel pending.</strong> No WalkieFleet account exists for
      this seat yet. That step is done by hand on the fleet server. Everything
      else in the CRM works normally.
    </div>
  )
}

function Clock() {
  const [now, setNow] = useState(() => new Date())
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000)
    return () => clearInterval(t)
  }, [])
  return <div className="clock">{now.toLocaleTimeString([], { hour12: false })}</div>
}

export function Shell({
  me, onSignedOut, panelOnly = false,
}: {
  me: Principal
  onSignedOut: () => void
  /** The pop-out window: the panel and nothing else. */
  panelOnly?: boolean
}) {
  const [view, setView] = useState<View>(panelOnly ? 'panel' : 'calls')
  /** The menu on his own name, and the password dialog it opens. */
  const [menuOpen, setMenuOpen] = useState(false)
  const [passwordOpen, setPasswordOpen] = useState(false)
  useEffect(() => {
    if (!menuOpen) return
    const shut = (e: Event) => {
      if (e instanceof KeyboardEvent && e.key !== 'Escape') return
      if (e instanceof MouseEvent && (e.target as Element | null)?.closest('.um-wrap')) return
      setMenuOpen(false)
    }
    window.addEventListener('mousedown', shut)
    window.addEventListener('keydown', shut)
    return () => { window.removeEventListener('mousedown', shut); window.removeEventListener('keydown', shut) }
  }, [menuOpen])
  /** The closed call Search asked to open under Past calls. */
  const [pastCallId, setPastCallId] = useState<string | null>(null)
  /** Which of the four is open inside Reports / History. Null is the
   *  customer's "please choose" -- every press of the tab starts there. */
  const [historyPick, setHistoryPick] = useState<HistoryPick | null>(null)
  const openPastCall = (id: string) => {
    setPastCallId(id); setHistoryPick('past'); setView('reports')
  }
  /** THE TOP BAR'S "+ New call" (9 Oct 2026): a fresh number per press,
   *  which Dispatch takes once and opens the form for. */
  const askSeq = useRef(0)
  const [newCallAsk, setNewCallAsk] = useState<number | null>(null)
  const newCallFromBar = () => {
    askSeq.current += 1
    setNewCallAsk(askSeq.current)
    setView('calls')
  }
  /** Unread texts, for the count on the Messaging tab. Polled only for a
   *  role that holds the tab, and silent when the server cannot be reached
   *  -- the strip it replaces was silent for the same reason: no dependency
   *  may block the screen. */
  const [unreadTexts, setUnreadTexts] = useState(0)
  /** THE FEEDBACK TAB (065): for a role that may report, or may see every
   *  report. The count is replies and status changes this login has not
   *  read; `cameFrom` is the screen he was on when he pressed the tab, so a
   *  report can say where it was sent from. */
  const mayFeedback = can('feedback.report') || can('feedback.view_all')
  const [unreadFeedback, setUnreadFeedback] = useState(0)
  const [cameFrom, setCameFrom] = useState('')
  useEffect(() => {
    if (!mayFeedback) return
    let alive = true
    const load = () =>
      api.feedbackUnread()
        .then((r) => { if (alive) setUnreadFeedback(r.unread) })
        .catch(() => {})
    load()
    const t = setInterval(load, 60_000)
    return () => { alive = false; clearInterval(t) }
  }, [mayFeedback])
  useEffect(() => {
    if (!can('messaging.view') && !can('messaging.view_all')) return
    let alive = true
    const load = () =>
      api.messagingUnread()
        .then((r) => { if (alive) setUnreadTexts(r.unread) })
        .catch(() => {})
    load()
    const t = setInterval(load, 20_000)
    return () => { alive = false; clearInterval(t) }
  }, [])

  // The panel lives here rather than inside a screen, so switching views does
  // not tear down the radio -- and so exactly one session exists.
  const [seat, setSeat] = useState<RadioSeat | null>(null)
  const [seats, setSeats] = useState<RadioSeat[]>([])
  const [talking, setTalking] = useState<PanelTx | null>(null)
  const [radioNote, setRadioNote] = useState<string | null>(null)
  const [transmit, setTransmit] = useState<((on: boolean) => void) | null>(null)
  const [setGroupFn, setSetGroupFn] = useState<((id: string) => void) | null>(null)
  const [groups, setGroups] = useState<PanelGroup[]>([])
  const [group, setGroup] = useState<string | null>(null)
  const [panelWindow, setPanelWindow] = useState<Window | null>(null)
  const [wire, setWire] = useState<{ at: string; type: string; raw: string }[]>([])

  const onSeat = useCallback((s: RadioSeat | null, all: RadioSeat[]) => {
    setSeat(s)
    setSeats(all)
  }, [])
  const onTx = useCallback((live: PanelTx | null) => setTalking(live), [])
  // Stored as a thunk: React would otherwise call a bare function passed to a
  // setter, and transmit immediately.
  const registerTransmit = useCallback(
    (fn: ((on: boolean) => void) | null) => setTransmit(() => fn), [])
  const registerSetGroup = useCallback(
    (fn: ((id: string) => void) | null) => setSetGroupFn(() => fn), [])
  const onWire = useCallback(
    (entry: { at: string; type: string; raw: string }) =>
      setWire((w) => [entry, ...w].slice(0, 60)), [])
  const onGroups = useCallback((list: PanelGroup[], selected: string | null) => {
    setGroups(list)
    setGroup(selected)
  }, [])

  // Opening the panel in its own window stands our copy down entirely. Two
  // panel sessions in one browser profile share a device id, and that is the
  // failure that silently empties recordings -- so it is not enough to grey
  // the buttons out.
  const popOut = useCallback(() => {
    if (panelWindow && !panelWindow.closed) {
      panelWindow.focus()
      return
    }
    const w = window.open(`${window.location.origin}/?panel=1`, 'adv-crm-panel')
    setPanelWindow(w)
  }, [panelWindow])

  useEffect(() => {
    if (!panelWindow) return
    const t = setInterval(() => {
      if (panelWindow.closed) {
        setPanelWindow(null)   // our panel comes back up
      }
    }, 1500)
    return () => clearInterval(t)
  }, [panelWindow])

  const initials = me.display_name
    .split(/\s+/)
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase()

  // An org admin is a dispatcher whose role has the Settings branch unlocked --
  // no separate build, no duplicated screens.
  // Cached once so any component can ask, rather than threading the principal
  // down through every screen that has a gated button on it.
  rememberPermissions(me.permissions)
  rememberCardDelivery(me.card_delivery)

  // A LOGIN THAT IS ONLY A DISPATCHER -- one whose top bar has no door but
  // Dispatch -- is spared the radio notices and the second header strip. The
  // customer, 17 Sep 2026: "I don't want the dispatcher should see how much
  // users and if any equipment is missing. This is more for higher end
  // dispatcher and also if any errors, they don't need to know it."
  // Administrators keep both for now: he is using them to test. Decided from
  // the same gates that draw the doors below, so the two cannot disagree.
  const dispatcherOnly = !(
    can('calls.past.review') || can('settings.organization') || can('search.calls')
    || canAny('search') || can('equipment.view') || canAny('reporting')
    || hasSettingsTabs()
  )

  return (
    <div className="shell">
      <div className="chrome">
        <div className="org-name">{me.organization.name}</div>

        <nav className="nav">
          {/* "+ NEW CALL" FIRST, ON EVERY SCREEN (the customer, 9 Oct 2026:
              "put the button new calls before dispatcher on top"; teal, his
              Q2 A). First in the bar, so when a narrow window wraps it, it is
              never the one pushed down. Taking a call needs no tick -- the
              column's own button has none -- so neither does this. NOT IN
              THE POP-OUT PANEL WINDOW: there it would swap the panel for a
              second Dispatch while the main window stays stood down, and
              the radio would be on neither screen. */}
          {!panelOnly && (
            <button type="button" className="btn primary bar-newcall" onClick={newCallFromBar}>
              + New call
            </button>
          )}
          <button
            className={`navlink ${view === 'calls' ? 'on' : ''}`}
            onClick={() => setView('calls')}
          >
            Dispatch
          </button>
          {/* Hidden rather than disabled when this customer has no dispatch
              panel at all. A permanently greyed-out button is a promise that
              something is missing; there is nothing missing here. */}
          {me.dispatch_state !== 'off' && (
            <button
              className={`navlink ${view === 'panel' ? 'on' : ''}`}
              onClick={() => setView('panel')}
              disabled={!seat}
              title={seat ? 'The full dispatch panel' : RADIO_REASON[me.dispatch_state]}
            >
              Radio panel
            </button>
          )}
          {/* THE MESSAGING TAB (items 20-24, 23 Sep 2026): texts in and out,
              for a role holding the tick. The count is what is unread across
              every conversation -- the strip above Open calls no longer
              carries it (item 20). Also for a role that may only send group
              messages (066): the tab then opens on Group messages. */}
          {mayMessagingTab() && (
            <button
              className={`navlink ${view === 'messaging' ? 'on' : ''}`}
              onClick={() => setView('messaging')}
            >
              Messaging{unreadTexts > 0 && <span className="navbadge">{unreadTexts}</span>}
            </button>
          )}
          {/* THE KEY THAT LOADS IT: `/api/status` needs the organization's
              settings, and the customer keeps this screen to administrators. */}
          {can('settings.organization') && (
            <button
              className={`navlink ${view === 'overview' ? 'on' : ''}`}
              onClick={() => setView('overview')}
            >
              Overview
            </button>
          )}
          {/* §09: one box for the whole system, used after calls rather than
              during them -- which is why it is its own view and not a field
              above the working call. Drawn only for a role holding at least
              one of the five search nodes. */}
          {canAny('search') && (
            <button
              className={`navlink ${view === 'search' ? 'on' : ''}`}
              onClick={() => setView('search')}
            >
              Search
            </button>
          )}
          {/* §24: both tools live in the admin section -- an org admin is a
              dispatcher with the branch unlocked. So this appears only for a
              role that holds one of them -- all THREE of them, which the
              hand-written pair of disjuncts did not: a role with member
              activity alone got no Reports button. */}
          {can('equipment.view') && (
            <button
              className={`navlink ${view === 'equipment' ? 'on' : ''}`}
              onClick={() => setView('equipment')}
            >
              Equipment
            </button>
          )}
          {/* REPORTS / HISTORY (6 Oct 2026): Past calls and Call log moved in
              here beside the two reports, at the customer's request -- Call
              log used to sit beside Dispatch for the start-of-shift "did I
              miss anybody", and that is one click further now, knowingly.
              Drawn when the role holds any of the four; each button inside
              keeps its own gate. Every press starts on "please choose". */}
          {mayReportsHistory() && (
            <button
              className={`navlink ${view === 'reports' ? 'on' : ''}`}
              onClick={() => { setHistoryPick(null); setPastCallId(null); setView('reports') }}
            >
              Reports / History
            </button>
          )}
          {/* Drawn when there is something behind it. What this section
              contains differs by role -- a dispatcher sees the roster, an
              administrator sees all nine tabs -- and a button opening an empty
              section would be a dead end. */}
          {hasSettingsTabs() && (
            <button
              className={`navlink ${view === 'settings' ? 'on' : ''}`}
              onClick={() => setView('settings')}
            >
              Settings
            </button>
          )}
          {mayFeedback && (
            <button
              className={`navlink ${view === 'feedback' ? 'on' : ''}`}
              onClick={() => {
                if (view !== 'feedback') {
                  setCameFrom(view === 'reports' && historyPick
                    ? `${VIEW_NAMES.reports} — ${HISTORY_NAMES[historyPick]}`
                    : VIEW_NAMES[view])
                }
                setView('feedback')
              }}
            >
              Feedback / Support{unreadFeedback > 0 && <span className="fb-badge">{unreadFeedback}</span>}
            </button>
          )}
        </nav>

        <div className="spacer" />
        <ThemeToggle />
        <Clock />
        {/* HIS NAME IS HIS MENU (10 Oct 2026): "a dispatcher should be able
            to change his password by clicking ... on his name". Sign out
            moved into it. */}
        <div className="um-wrap">
          <button type="button" className="who um-who" aria-haspopup="menu" aria-expanded={menuOpen}
                  onClick={() => setMenuOpen((o) => !o)}>
            <div className="av">{initials}</div>
            <div>
              <div style={{ fontSize: 13 }}>{me.display_name}</div>
              <div className="r">{me.role_name ?? 'No role'}</div>
            </div>
            <span className="um-caret" aria-hidden="true">▾</span>
          </button>
          {menuOpen && (
            <div className="um-menu" role="menu">
              <button type="button" role="menuitem"
                      onClick={() => { setMenuOpen(false); setPasswordOpen(true) }}>
                Change my password
              </button>
              <button type="button" role="menuitem"
                      onClick={() => api.signOut().finally(onSignedOut)}>
                Sign out
              </button>
            </div>
          )}
        </div>
      </div>
      {passwordOpen && <ChangePassword onClose={() => setPasswordOpen(false)} />}
      <StaySignedIn onSignedOut={onSignedOut} />

      {/* Say WHICH of the three reasons it is. They send somebody to three
          different places, and the wrong one wastes an afternoon on a fleet
          server looking for an account that was never the problem. NOT to a
          login that is only a dispatcher: none of the three is his to fix,
          and a standing notice above every shift is one more thing to read
          past. */}
      {me.dispatch_pending && !dispatcherOnly && <RadioBanner state={me.dispatch_state} />}

      {/* §07: always visible so anyone on duty can see something is out, and
          quiet unless a reminder date has arrived. It renders nothing at all
          when nothing is out -- a working screen does not need a line saying
          so. */}
      {view === 'calls' && <OutNowStrip onOpen={() => setView('equipment')} />}

      {view === 'calls' && (
        <Dispatch
          me={me}
          seat={seat}
          seats={seats}
          talking={talking}
          radioNote={radioNote}
          transmit={transmit}
          groups={groups}
          group={group}
          onGroup={(id) => { setGroup(id); setGroupFn?.(id) }}
          panelElsewhere={!!panelWindow && !panelWindow.closed}
          onPopOut={popOut}
          newCallAsk={newCallAsk}
          onNewCallTaken={() => setNewCallAsk(null)}
        />
      )}
      {view === 'overview' && <Overview />}
      {view === 'search' && (
        <Search onOpenCall={can('calls.past.review') ? openPastCall : undefined} />
      )}
      {view === 'messaging' && (
        <MessagingTab
          me={me}
          unread={unreadTexts}
          onOpenCall={(id, open) => {
            // An open call is on the board; a closed one is under Past calls
            // for a role that may review it.
            if (open) { setView('calls'); return }
            if (can('calls.past.review')) openPastCall(id)
          }}
          onRead={() =>
            api.messagingUnread().then((r) => setUnreadTexts(r.unread)).catch(() => {})}
        />
      )}
      {view === 'feedback' && (
        <Feedback
          cameFrom={cameFrom}
          onRead={() =>
            api.feedbackUnread().then((r) => setUnreadFeedback(r.unread)).catch(() => {})}
        />
      )}
      {view === 'reports' && (
        // A button press is a fresh choice: the call Search or Messaging
        // opened must not come back on a later press of Past calls.
        <ReportsHistory pick={historyPick} pastCallId={pastCallId}
                        onPick={(p) => { setPastCallId(null); setHistoryPick(p) }} />
      )}
      {view === 'equipment' && <Equipment />}
      {view === 'settings' && <Settings />}

      {/* Always mounted once a seat is held: a microphone that has not
          connected cannot be keyed, and the dispatcher must be able to talk
          without opening this. Revealed in place rather than in a second
          window -- two panel windows in one browser share a device id, which
          is what makes recordings come back empty. */}
      {view === 'panel' && <WireLog wire={wire} />}

      <PanelHost
        enabled={me.dispatch_state !== 'off'}
        visible={view === 'panel'}
        standDown={!!panelWindow && !panelWindow.closed}
        onSeat={onSeat}
        onTx={onTx}
        onNote={setRadioNote}
        onGroups={onGroups}
        onWire={onWire}
        registerTransmit={registerTransmit}
        registerSetGroup={registerSetGroup}
      />
    </div>
  )
}
